import AnalysisToolService, {
  ICaptures
} from '../../services/AnalysisToolService';
import { Button, ClickAwayListener } from '@mui/material';
import {
  LessonComponentResponseType,
  ScreenCaptureComponent
} from '../../models/LessonComponent';
import React, { useEffect, useRef, useState } from 'react';
import { cloneDeep, isArray, isNull } from 'lodash';

import AnalysisToolComponent from '../video_analysis_tool/AnalysisToolComponent';
import AssetService from '../../../shared/services/AssetService';
import { AssetType } from '../../../shared/models/Asset';
import AuthService from '../../../shared/services/AuthService';
import { CustomLoadingProgress } from '../../../shared/components/CustomLoadingProgress';
import Grid from '@mui/material/Unstable_Grid2';
import LessonComponentService from '../../services/LessonComponentService';
import { ScreenCaptureItem } from '../../models/LessonComponentContent';
import { ScreenCaptureVideo } from './ScreenCaptureVideo';
import { nanoid } from 'nanoid';
import screenCaptureStyles from '../../styles/ScreenCapture.module.css';
import { useAssetStorage } from '../../../shared/hooks/useAssetStorage';
import useAssets from '../../../shared/hooks/useAssets';
import { useLessonComponentContext } from '../../models/LessonComponentContext';
import { useLessonContext } from '../../models/LessonContext';
import { useLessonEditContext } from '../../models/LessonEditContext';
import { useLessonPlayerContext } from '../../../dashboard/models/LessonPlayerContext';
import { useLessonPlayerMarkingContext } from '../../../assessments/models/LessonPlayerMarkingContext';

interface ScreenCaptureComparisonProps {
  component: ScreenCaptureComponent;
  onChange: (response: LessonComponentResponseType) => void;
}

export const ScreenCaptureComparison = (
  props: ScreenCaptureComparisonProps
) => {
  const videoUrl = props.component.settings.assetkey || null;
  const tool = useRef<AnalysisToolComponent>();
  const { lessonState } = useLessonPlayerContext();
  const { handleSettingsChange } = useLessonEditContext();
  const { correct, reattempt, showFeedback, answerSaved } =
    useLessonComponentContext();
  const { mode, finalized, quizLocked, assessmentSubmitted, portfolioLocked } =
    useLessonContext();
  const { markerUserkey } = useLessonPlayerMarkingContext();
  const learnerAnswer: ScreenCaptureItem[] = props.component.answer
    ? JSON.parse(props.component.answer)
    : null;
  const authorAnswer = props.component.content.analysisTool || null;
  const owner =
    mode === 'mark' ? lessonState?.answerParams.userkey : AuthService.Userkey;
  const { uploadCaptures } = useAssetStorage(AuthService.Tenant);
  const [isItemCaptured, setIsItemCaptured] = useState(false);
  const [learnerItems, setLearnerItems] = useState<ScreenCaptureItem[]>([]);
  const [authorItems, setAuthorItems] = useState<ScreenCaptureItem[]>([]);
  const [currentShowItem, setCurrentShowItem] = useState<ScreenCaptureItem>();
  const [capturing, setCapturing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteItems, setDeleteItems] = useState<string[]>([]);

  const type: AssetType[] = ['image'];
  const { deleteAssets } = useAssets(
    type,
    'learner',
    false /* no need to load assets */
  );

  /** Save learner's image to azure when 'Save And Continue' is clicked.
   * Load Authors image from azure.
   */
  useEffect(() => {
    if (mode === 'learn' && answerSaved) {
      uploadCaptures(learnerItems, 'learner_assets');
      deleteAssets(deleteItems, 'learner');
    }

    if (authorAnswer) {
      const fetchProps: ICaptures = {
        captures: authorAnswer.captures,
        setLoading,
        setItems: setAuthorItems
      };
      AnalysisToolService.fetchItems(fetchProps);
    }
  }, [answerSaved]);

  /** Set learner's saved images if any */
  useEffect(() => {
    if (
      learnerAnswer &&
      (mode === 'learn' || mode === 'mark') &&
      !isItemCaptured
    ) {
      const fetchProps: ICaptures = {
        // Earlier answer wasn't an array. It was an object.
        captures: isArray(learnerAnswer) ? learnerAnswer : [learnerAnswer],
        owner,
        setLoading,
        setItems: setLearnerItems
      };
      AnalysisToolService.fetchItems(fetchProps);
    }
  }, [mode, props.component.answer]);

  /** Convert Author/Learner answer as string */
  const getStringifiedAnswer = (items: ScreenCaptureItem[]) => {
    return JSON.stringify(
      items.map((item) => {
        return { id: item.id, state: item.state };
      })
    );
  };

  /** Handle Screen Capture click */
  const handleCapture = async () => {
    setCapturing(true);
    setIsItemCaptured(true);
    const { base64, state } = await tool.current?.triggerCapture();
    const id = nanoid();
    const copyItems: ScreenCaptureItem[] = [
      {
        id,
        image: base64,
        state: JSON.stringify(state)
      }
    ]; // Current specification is 1 item.

    if (mode === 'edit') {
      setAuthorItems(copyItems);
      const copy = cloneDeep(props.component);
      copy.content.analysisTool.captures = copyItems.map((item) => {
        return { id: item.id, state: item.state };
      });
      uploadCaptures(copyItems, 'author_assets');
      handleSettingsChange(copy);
    } else {
      setDeleteItems([...deleteItems, learnerItems[0]?.id]); // Current specification is 1 item.
      setLearnerItems(copyItems);
      props.onChange(getStringifiedAnswer(copyItems));
    }
    setCapturing(false);
  };

  const handleImageClick = (item: ScreenCaptureItem) => {
    tool.current?.showImage(item.image);
    setCurrentShowItem(item);
  };

  /** Handle mouse click away */
  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    // It fires with 'touchend' and 'click' event on touch devices.
    // The 'touchend' fires before 'click',
    if (event.type === 'touchend') {
      return;
    }
    //Check the mouse click on current item's image or not
    const clickEventImage = event.target['alt'];
    if (
      mode !== 'mark' &&
      clickEventImage !== '' &&
      clickEventImage !== currentShowItem?.id
    ) {
      tool.current?.hideImage();
      setCurrentShowItem(null);
    }
  };

  const canEditCapture =
    (LessonComponentService.canSubmit(mode) &&
      !finalized &&
      !assessmentSubmitted &&
      !quizLocked) ||
    mode === 'edit';

  /**
   * Returns a boolean value indicating whether to show author image,
   * depending on the lesson type, lesson state and mode.
   */
  const showAuthorItem = () => {
    if (
      // No author item until the user has answered. By default "correct" value would be null.
      isNull(correct) ||
      // Hide author item when "Reattempt" is true AND the lesson is not finalized.
      (reattempt && !finalized)
    ) {
      return false;
    }
    return showFeedback;
  };

  /** Show captured / saved image for both author and learner */
  const ShowAnswerImage = (props: {
    isAuthor?: boolean;
    item: ScreenCaptureItem;
  }) => {
    const item = props.item;
    return (
      <Grid className={screenCaptureStyles.carouselItemsContainer}>
        <ClickAwayListener onClickAway={handleClickAway}>
          <img
            src={item?.image}
            alt={item?.id}
            className={screenCaptureStyles.imgContainer}
            onClick={() => handleImageClick(item)}
          />
        </ClickAwayListener>
        <span className={screenCaptureStyles.imageLabelProp}>
          {props.isAuthor
            ? mode !== 'edit' && 'Exemplar Answer'
            : mode !== 'mark'
            ? showAuthorItem() && 'Your Answer'
            : `Learner's Answer`}
        </span>
      </Grid>
    );
  };

  return (
    <>
      {!videoUrl ? (
        <ScreenCaptureVideo videoUrl={videoUrl} />
      ) : (
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid className={screenCaptureStyles.videoTool}>
            <AnalysisToolComponent
              ref={tool}
              mediaUrl={AssetService.getAssetUrl(videoUrl)}
            />
          </Grid>
          {canEditCapture && (
            <Grid className={screenCaptureStyles.captureButton}>
              <Button
                color="primary"
                variant="contained"
                disabled={
                  capturing ||
                  (!isNull(correct) && !reattempt) ||
                  portfolioLocked
                }
                onClick={handleCapture}
              >
                {!learnerAnswer || mode === 'edit'
                  ? 'Screen Capture'
                  : 'Replace Screen Capture'}
              </Button>
            </Grid>
          )}
          {loading && <CustomLoadingProgress />}
          <Grid container wrap="nowrap">
            {learnerItems.map(
              (item) =>
                mode !== 'edit' &&
                props.component.answer && (
                  <ShowAnswerImage key={item.id} item={item} />
                )
            )}
            {authorItems.map(
              (item) =>
                (mode === 'edit' || markerUserkey || showAuthorItem()) && (
                  <ShowAnswerImage key={item.id} isAuthor item={item} />
                )
            )}
          </Grid>
        </Grid>
      )}
    </>
  );
};
